/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {
  PageState
} from '@ion/applications/src/types/pages';
import {
  AdminAction,
  AsyncState
} from '@ion/applications/src/types/redux';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  DeviceConfigsFilters,
  DeviceConfiguration
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  ADD_ADMIN_DEVICE_CONFIG_ERROR,
  ADD_ADMIN_DEVICE_CONFIG_REQUEST,
  ADD_ADMIN_DEVICE_CONFIG_RESPONSE,
  ADMIN_DEVICE_CONFIGS_GRID_REFRESHED,
  AUDIT_ADMIN_DEVICE_CONFIG,
  AUDIT_ADMIN_DEVICE_CONFIG_ERROR,
  AUDIT_ADMIN_DEVICE_CONFIG_REQUEST,
  AUDIT_ADMIN_DEVICE_CONFIG_RESPONSE,
  AUDIT_ADMIN_DEVICE_CONFIG_SHOW_CHANGES_DIALOG,
  DELETE_ADMIN_DEVICE_CONFIG_ERROR,
  DELETE_ADMIN_DEVICE_CONFIG_REQUEST,
  DELETE_ADMIN_DEVICE_CONFIG_RESET,
  DELETE_ADMIN_DEVICE_CONFIG_RESPONSE,
  DISPLAY_DELETE_DEVICE_CONFIG_DIALOG,
  EDIT_ADMIN_DEVICE_CONFIG_ERROR,
  EDIT_ADMIN_DEVICE_CONFIG_REQUEST,
  EDIT_ADMIN_DEVICE_CONFIG_RESPONSE,
  GET_ADMIN_DEVICE_CONFIGS_ERROR,
  GET_ADMIN_DEVICE_CONFIGS_REQUEST,
  GET_ADMIN_DEVICE_CONFIGS_RESPONSE,
  INITIALIZE_ADMIN_DEVICE_CONFIGS,
  RESET_DEVICE_CONFIGS_SEARCH_QUERIES,
  SET_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  SHOW_ADD_DEVICE_CONFIG_DIALOG,
  SHOW_EDIT_DEVICE_CONFIG_DIALOG,
  UPDATE_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS,
  UPDATE_DEVICE_CONFIGS_SEARCH_QUERIES
} from '../actions/admin-deviceconfigs';

// Defines the user preference key for the Admin Device Configs page
export const PREFERENCE = 'admin-deviceconfigs';

export interface AdminDeviceConfigsAction extends AdminAction {
  deviceConfig?: DeviceConfiguration;
  searchParams?: DeviceConfigsFilters;
  showDialog?: boolean;
  isView?: boolean;
};

export interface AdminDeviceConfigsDestroyState extends AsyncState {
  selected: DeviceConfiguration | null;
  result: DeviceConfiguration | null;
  showDialog: boolean;
};

interface DeviceConfigsActionState extends AsyncState {
  selected: DeviceConfiguration | null;
  result: DeviceConfiguration | null;
  showDialog: boolean;
  isView?: boolean;
};

export interface AdminDeviceConfigsAuditState extends DeviceConfigsActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface AdminDeviceConfigsState extends PageState {
  deviceConfigs: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    pageSize: number
  }
  destroy: AdminDeviceConfigsDestroyState,
  search: DeviceConfigsFilters,
  add: DeviceConfigsActionState,
  edit: DeviceConfigsActionState,
  audit: AdminDeviceConfigsAuditState
};

export const initialState: AdminDeviceConfigsState = {
  isInitialized : false,
  deviceConfigs : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    pageSize            : gridDefaultPageSize
  },
  destroy : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  search : {
    name        : null,
    deviceType  : null,
    pegBehavior : null
  },
  add : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  edit : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isView     : false
  },
  audit : {
    showDialog        : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  }
};

const reducers = {
  [INITIALIZE_ADMIN_DEVICE_CONFIGS] : (draft: AdminDeviceConfigsState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.search.name              = null;
    draft.search.deviceType        = null;
    draft.search.pegBehavior       = null;
    draft.destroy.error            = null;
  },
  [ADMIN_DEVICE_CONFIGS_GRID_REFRESHED] : ({destroy, add, edit, audit}: AdminDeviceConfigsState) => {
    destroy.result = add.result = edit.result = audit.result = null;
  },
  [SET_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS] : ({grid, deviceConfigs}: AdminDeviceConfigsState, {columnState}) => {
    onSetGridColumns({
      isLoading  : deviceConfigs.loading,
      preference : PREFERENCE,
      grid
    }, columnState);
  },
  [UPDATE_ADMIN_DEVICE_CONFIGS_GRID_COLUMNS] : ({grid}: AdminDeviceConfigsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_ADMIN_DEVICE_CONFIGS_REQUEST] : ({deviceConfigs}: AdminDeviceConfigsState) => {
    deviceConfigs.loading = true;
  },
  [GET_ADMIN_DEVICE_CONFIGS_RESPONSE] : ({deviceConfigs}: AdminDeviceConfigsState) => {
    deviceConfigs.loading = false;
  },
  [GET_ADMIN_DEVICE_CONFIGS_ERROR] : ({deviceConfigs}: AdminDeviceConfigsState, {error}: AdminDeviceConfigsAction) => {
    deviceConfigs.loading = false;
    deviceConfigs.error   = error;
  },
  [DISPLAY_DELETE_DEVICE_CONFIG_DIALOG] : (
    {destroy}: AdminDeviceConfigsState,
    {deviceConfig, show}: AdminDeviceConfigsAction
  ) => {
    destroy.selected = show ? deviceConfig : null;
    destroy.showDialog = show;
    destroy.result = null;
    destroy.error = null;
  },
  [DELETE_ADMIN_DEVICE_CONFIG_REQUEST] : ({destroy}: AdminDeviceConfigsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_DEVICE_CONFIG_RESPONSE] : (
    {destroy}: AdminDeviceConfigsState,
    {deviceConfig}: AdminDeviceConfigsAction
  ) => {
    destroy.result = deviceConfig;
    destroy.selected = null;
    destroy.loading = destroy.showDialog = false;
  },
  [DELETE_ADMIN_DEVICE_CONFIG_ERROR] : ({destroy}: AdminDeviceConfigsState, {error}: AdminDeviceConfigsAction) => {
    destroy.error = error;
    destroy.result = null;
    destroy.loading = destroy.showDialog = false;
  },
  [DELETE_ADMIN_DEVICE_CONFIG_RESET] : ({destroy}: AdminDeviceConfigsState) => {
    destroy.error = destroy.result = null;
  },
  [UPDATE_DEVICE_CONFIGS_SEARCH_QUERIES] : (
    {search}: AdminDeviceConfigsState,
    {searchParams}: AdminDeviceConfigsAction
  ) => {
    search.name = searchParams.name ?? null;
    search.deviceType = searchParams.deviceType ?? null;
    search.pegBehavior = searchParams.pegBehavior ?? null;
  },
  [RESET_DEVICE_CONFIGS_SEARCH_QUERIES] : ({search}: AdminDeviceConfigsState) => {
    search.name = null;
    search.deviceType = null;
    search.pegBehavior = null;
  },
  [SHOW_ADD_DEVICE_CONFIG_DIALOG] : ({add}: AdminDeviceConfigsState, {showDialog}: AdminDeviceConfigsAction) => {
    add.showDialog = showDialog;
  },
  [ADD_ADMIN_DEVICE_CONFIG_REQUEST] : ({add}: AdminDeviceConfigsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_DEVICE_CONFIG_RESPONSE] : ({add}: AdminDeviceConfigsState, {deviceConfig}: AdminDeviceConfigsAction) => {
    add.result = deviceConfig;
    add.selected = null;
    add.loading = add.showDialog = false;
  },
  [ADD_ADMIN_DEVICE_CONFIG_ERROR] : ({add}: AdminDeviceConfigsState, {error}: AdminDeviceConfigsAction) => {
    add.error = error;
    add.result = null;
    add.loading = add.showDialog = false;
  },
  [SHOW_EDIT_DEVICE_CONFIG_DIALOG] : (
    {edit}: AdminDeviceConfigsState,
    {showDialog, deviceConfig, isView}: AdminDeviceConfigsAction
  ) => {
    edit.selected      = showDialog ? deviceConfig : null;
    edit.result        = null;
    edit.showDialog    = showDialog;
    edit.isView        = isView;
  },
  [EDIT_ADMIN_DEVICE_CONFIG_REQUEST] : ({edit}: AdminDeviceConfigsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_DEVICE_CONFIG_RESPONSE] : (
    {edit}: AdminDeviceConfigsState,
    {deviceConfig}: AdminDeviceConfigsAction
  ) => {
    edit.result = deviceConfig;
    edit.selected = null;
    edit.loading = edit.showDialog = false;
  },
  [EDIT_ADMIN_DEVICE_CONFIG_ERROR] : ({edit}: AdminDeviceConfigsState, {error}: AdminDeviceConfigsAction) => {
    edit.error = error;
    edit.result = edit.selected = null;
    edit.loading = edit.showDialog = false;
  },
  [AUDIT_ADMIN_DEVICE_CONFIG] : ({audit}: AdminDeviceConfigsState, {deviceConfig, show}: AdminDeviceConfigsAction) => {
    audit.selected = show ? deviceConfig : null;
    audit.showDialog = show;
    audit.result = null;
  },
  [AUDIT_ADMIN_DEVICE_CONFIG_REQUEST] : ({audit}: AdminDeviceConfigsState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_ADMIN_DEVICE_CONFIG_RESPONSE] : ({audit}: AdminDeviceConfigsState, {result}: AdminDeviceConfigsAction) => {
    audit.loading = false;
    audit.showDialog = false;
    audit.result = result;
  },
  [AUDIT_ADMIN_DEVICE_CONFIG_ERROR] : ({audit}: AdminDeviceConfigsState, {error}: AdminDeviceConfigsAction) => {
    audit.loading = false;
    audit.showDialog = false;
    audit.error = error;
  },
  [AUDIT_ADMIN_DEVICE_CONFIG_SHOW_CHANGES_DIALOG] : ({audit}: AdminDeviceConfigsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminDeviceConfigsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

export default produce((draft: AdminDeviceConfigsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
